import {HttpClient, HttpParams} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FlowBaseUrlEnum } from '@constants/app.constants';
import {
  Booking,
  BookingPaymentResult,
  CommitRequestV2,
  N0CalendarInvite,
  N0CommitRequest,
} from '@models/booking.models';
import { ApiResponse } from '@models/responses/api-response.model';
import { EnvironmentService } from '@services/environment-service/environment.service';
import { Observable } from 'rxjs';
import { AbandonedCartService } from "@services/abandoned-cart/abandoned-cart.service";
import {GrowthBookService} from "@services/growthbook/growthbook.service";

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  private static readonly CONTROLLER_ROOT = 'v1/booking';
  private static readonly CONTROLLER_ROOT_V2 = 'v2/booking';
  private static readonly RETRIEVE_ROOT = 'v1/retrieve';
  private static readonly TRIPS_ROOT = 'v1/trip';

  constructor(private httpClient: HttpClient, private readonly abandonedCartService: AbandonedCartService, private gbs: GrowthBookService) {}

  getBookingInState(): Observable<ApiResponse<Booking>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${BookingService.CONTROLLER_ROOT}`;
    return this.httpClient.get<ApiResponse<Booking>>(endpoint);
  }

  commitBooking(): Observable<ApiResponse<null>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${BookingService.CONTROLLER_ROOT}`;
    return this.httpClient.post<ApiResponse<null>>(endpoint, {
      notifyContacts: true,
    });
  }

  completeBooking(): Observable<ApiResponse<BookingPaymentResult>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${BookingService.CONTROLLER_ROOT}/complete`;
    let params = new HttpParams();
    params = params.set('abandonedCartVariant', this.gbs.getFeatureVariant('feat-001220-abandoned-cart', ''));
    params = params.set('bookingConfirmationVariant', this.gbs.getFeatureVariant('feat-002785-booking-confirmation-via-brevo', ''));
    return this.httpClient.post<ApiResponse<BookingPaymentResult>>(endpoint, {
      notifyContacts: true,
    },
    { params }
    );
  }

  getBookingPaymentStatus(
    bookingFlow: FlowBaseUrlEnum,
  ): Observable<ApiResponse<BookingPaymentResult>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${BookingService.CONTROLLER_ROOT}/bookingpolling?bookingFlow=${bookingFlow}&notifyContacts=true`;
    let params = new HttpParams();
    params = params.set('bookingConfirmationVariant', this.gbs.getFeatureVariant('feat-002785-booking-confirmation-via-brevo', ''));

    return this.httpClient.get<ApiResponse<BookingPaymentResult>>(endpoint, { params });
  }

  updateBooking(
    bookingFlow: FlowBaseUrlEnum,
    doNotifyContacts = true,
  ): Observable<ApiResponse<BookingPaymentResult>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${BookingService.CONTROLLER_ROOT}`;
    return this.httpClient.put<ApiResponse<BookingPaymentResult>>(endpoint, {
      bookingFlow,
      commitRequest: {
        notifyContacts: doNotifyContacts,
      } as CommitRequestV2,
    } as N0CommitRequest);
  }

  getRetrieveBooking(
    confirmationNumber: string,
    lastName: string,
  ): Observable<ApiResponse<Booking>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${
      BookingService.RETRIEVE_ROOT
    }${`?pnr=${confirmationNumber}&lastName=${lastName}`}`;
    return this.httpClient.get<ApiResponse<Booking>>(endpoint);
  }

  addToCalendar(): Observable<ApiResponse<N0CalendarInvite>> {
    return this.httpClient.get<ApiResponse<N0CalendarInvite>>(
      `${EnvironmentService.environment.apiBaseUrl}${BookingService.CONTROLLER_ROOT}/calendar/invite`,
    );
  }

  cancelFlights(journeyKeys: string[]): Observable<ApiResponse<Booking>> {
    const endpoint = `${EnvironmentService.environment.apiBaseUrl}${BookingService.TRIPS_ROOT}/canceljourneys`;
    return this.httpClient.post<ApiResponse<Booking>>(endpoint, journeyKeys);
  }

  deleteBookingInState(): Observable<ApiResponse<unknown>> {
    return this.httpClient.delete<ApiResponse<unknown>>(
      `${EnvironmentService.environment.apiBaseUrl}${BookingService.CONTROLLER_ROOT}`,
    );
  }

  checkDisruption(): Observable<
    ApiResponse<{
      status: string;
      activeSession: boolean;
      sessionToken?: string;
    }>
  > {
    return this.httpClient.post<
      ApiResponse<{
        status: string;
        activeSession: boolean;
        sessionToken?: string;
      }>
    >(
      `${EnvironmentService.environment.apiBaseUrl}v1/Levarti/disruption`,
      null,
    );
  }
}
